import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, CalendarDays, Plus, Trash2, ChevronDown, ChevronUp,
  Loader2, FileText, Upload, Users, Package, DollarSign, Sparkles,
  MapPin, Clock, Check, X, Share2, Download,
} from 'lucide-react';
import api from '../lib/api';
import ImportModal from '../components/ImportModal';

// ── helpers ───────────────────────────────────────────────────────────────────
function fmtTime(t) {
  if (!t) return '';
  try {
    const [h, m] = t.split(':').map(Number);
    return `${((h % 12) || 12)}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
  } catch { return t; }
}
function fmtRange(a, b) {
  if (!a) return 'Dates TBD';
  const opt = { month: 'short', day: 'numeric' };
  try {
    const sa = new Date(a + 'T00:00:00').toLocaleDateString('en-US', opt);
    if (!b || b === a) return sa;
    return `${sa} – ${new Date(b + 'T00:00:00').toLocaleDateString('en-US', { ...opt, year: 'numeric' })}`;
  } catch { return a; }
}
function countdown(start) {
  if (!start) return null;
  const days = Math.ceil((new Date(start + 'T00:00:00') - new Date()) / 86400000);
  if (days > 1) return `${days} days away`;
  if (days === 1) return 'Tomorrow';
  if (days === 0) return 'Today!';
  return null;
}
// Safely extract a string message from an axios error. FastAPI 422s return an array
// of error objects in `detail`; passing that to toast.error() would crash the render.
function errMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
}

// ── styling constants ─────────────────────────────────────────────────────────
const INPUT = 'w-full win-input px-3 py-2 text-sm';
const BTN_PRIMARY = 'flex items-center gap-2 px-4 py-2 win-btn win-btn-default text-sm font-semibold disabled:cursor-not-allowed';
const BTN_SM = 'flex items-center gap-1.5 px-3 py-1.5 win-btn text-xs';
const BTN_GHOST = 'flex items-center gap-1 text-xs';

// Keys match the lowercase type values the backend emits. All types share one
// neutral square bevel chip — Win98 has no color-coded category pills.
const TYPE_COLORS = {
  panel: 'win-well',
  photoshoot: 'win-well',
  signing: 'win-well',
  workshop: 'win-well',
  show: 'win-well',
  screening: 'win-well',
  gaming: 'win-well',
  meetup: 'win-well',
  social: 'win-well',
  contest: 'win-well',
  '18+': 'win-well',
  other: 'win-well',
};
const TYPE_LABELS = {
  panel: 'Panel', photoshoot: 'Photoshoot', signing: 'Signing', workshop: 'Workshop',
  show: 'Show', screening: 'Screening', gaming: 'Gaming', meetup: 'Meetup',
  social: 'Social', contest: 'Contest', '18+': '18+', other: 'Other',
};
const typeColor = (t) => TYPE_COLORS[t] || TYPE_COLORS.other;
const typeLabel = (t) => TYPE_LABELS[t] || (t ? t.charAt(0).toUpperCase() + t.slice(1) : '');

const TABS = [
  { key: 'browse', label: 'Schedule', icon: CalendarDays },
  { key: 'plan', label: 'My Plan', icon: FileText },
  { key: 'wearing', label: 'Wearing', icon: Package },
  { key: 'squad', label: 'Squad', icon: Users },
  { key: 'packing', label: 'Packing', icon: Package },
  { key: 'budget', label: 'Budget', icon: DollarSign },
];

// ── FilterPill ────────────────────────────────────────────────────────────────
function FilterPill({ label, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1 text-xs font-medium whitespace-nowrap ${active ? 'win-selected' : ''}`}>
      {label}
    </button>
  );
}

// ── EventRow ──────────────────────────────────────────────────────────────────
function EventRow({ ev, inPlan, onAdd, onRemove }) {
  const [open, setOpen] = useState(false);
  const typeClass = typeColor(ev.type);
  return (
    <div className="glass-card p-3">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            {ev.type && (
              <span className={`px-2 py-0.5 text-xs font-medium ${typeClass}`}>{typeLabel(ev.type)}</span>
            )}
            {ev.day && <span className="text-xs">{ev.day}</span>}
          </div>
          <p className="text-sm font-semibold leading-snug">{ev.title}</p>
          <div className="flex flex-wrap gap-2 mt-1 text-xs">
            {ev.start_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {fmtTime(ev.start_time)}{ev.end_time ? ` – ${fmtTime(ev.end_time)}` : ''}
              </span>
            )}
            {ev.location && (
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {inPlan ? (
            <button onClick={() => onRemove(ev.id)}
              className="flex items-center gap-1 px-2.5 py-1 text-xs win-selected">
              <Check className="w-3.5 h-3.5" /> Saved
            </button>
          ) : (
            <button onClick={() => onAdd(ev.id)} className={BTN_GHOST}>
              <Plus className="w-3.5 h-3.5" /> Plan
            </button>
          )}
          <button onClick={() => setOpen((o) => !o)} className={BTN_GHOST}>
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      {open && ev.description && (
        <p className="text-xs mt-2 pt-2 border-t border-[var(--win-shadow)] leading-relaxed">
          {ev.description}
        </p>
      )}
    </div>
  );
}

// ── BrowseTab ────────────────────────────────────────────────────────────────
function BrowseTab({ conId, events, plan, reloadPlan, onImport }) {
  const [typeFilter, setTypeFilter] = useState('All');
  const [dayFilter, setDayFilter] = useState('All');
  const [search, setSearch] = useState('');

  const types = ['All', ...Array.from(new Set(events.map((e) => e.type).filter(Boolean))).sort()];
  const days = ['All', ...Array.from(new Set(events.map((e) => e.day).filter(Boolean)))];
  // The plan is a list of enriched events (keyed by their own event id).
  const planMap = Object.fromEntries((plan || []).map((p) => [p.id, p]));

  const visible = events.filter((ev) => {
    if (typeFilter !== 'All' && ev.type !== typeFilter) return false;
    if (dayFilter !== 'All' && ev.day !== dayFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!ev.title?.toLowerCase().includes(q) && !ev.description?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const addToPlan = async (eventId) => {
    try {
      await api.post(`/cons/${conId}/my-schedule`, { con_event_id: eventId });
      await reloadPlan();
      toast.success('Added to your plan');
    } catch (err) {
      toast.error(errMsg(err, 'Could not add'));
    }
  };

  const removeFromPlan = async (eventId) => {
    try {
      await api.delete(`/cons/${conId}/my-schedule/${eventId}`);
      await reloadPlan();
    } catch (err) {
      toast.error(errMsg(err, 'Could not remove'));
    }
  };

  if (events.length === 0) {
    return (
      <div className="glass-card p-8 text-center">
        <Sparkles className="w-8 h-8 mx-auto mb-3" />
        <p className="font-semibold mb-1">No schedule yet</p>
        <p className="text-sm mb-4">
          Import a screenshot, PDF, or URL and the AI will extract the events.
        </p>
        <button onClick={onImport} className={`${BTN_PRIMARY} mx-auto`}>
          <Upload className="w-4 h-4" /> Import schedule
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="sticky top-0 z-20 py-3 bg-[var(--win-face)]">
        <input className={INPUT} placeholder="Search events…"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="flex gap-1.5 mt-2 overflow-x-auto scrollbar-hide pb-1">
          {types.map((t) => <FilterPill key={t} label={t === 'All' ? 'All' : typeLabel(t)} active={typeFilter === t} onClick={() => setTypeFilter(t)} />)}
        </div>
        {days.length > 2 && (
          <div className="flex gap-1.5 mt-1.5 overflow-x-auto scrollbar-hide pb-1">
            {days.map((d) => <FilterPill key={d} label={d} active={dayFilter === d} onClick={() => setDayFilter(d)} />)}
          </div>
        )}
      </div>
      <div className="space-y-2 mt-1">
        {visible.length === 0
          ? <p className="text-center text-sm py-8">No events match your filters</p>
          : visible.map((ev) => (
              <EventRow key={ev.id} ev={ev}
                inPlan={!!planMap[ev.id]}
                onAdd={addToPlan} onRemove={removeFromPlan} />
            ))}
      </div>
      <button onClick={onImport} className={`${BTN_SM} mt-4`}>
        <Upload className="w-3.5 h-3.5" /> Import more events
      </button>
    </div>
  );
}

// ── MyPlanTab ─────────────────────────────────────────────────────────────────
function MyPlanTab({ conId, plan, reloadPlan }) {
  // The plan is already a list of enriched events.
  const byDay = {};
  for (const ev of (plan || [])) {
    const day = ev.day || 'Unscheduled';
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(ev);
  }

  const removeFromPlan = async (eventId) => {
    try {
      await api.delete(`/cons/${conId}/my-schedule/${eventId}`);
      await reloadPlan();
    } catch (err) {
      toast.error(errMsg(err, 'Could not remove'));
    }
  };

  const days = Object.keys(byDay);

  if (days.length === 0) {
    return (
      <div className="glass-card p-8 text-center">
        <FileText className="w-8 h-8 mx-auto mb-3" />
        <p className="font-semibold">Your plan is empty</p>
        <p className="text-sm mt-1">Go to Schedule and hit + Plan on events you want to attend.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {days.map((day) => (
        <div key={day}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2">{day}</p>
          <div className="space-y-2">
            {byDay[day]
              .sort((a, b) => (a?.start_time || '').localeCompare(b?.start_time || ''))
              .map((ev) => (
                <div key={ev.id} className="glass-card p-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {ev?.type && (
                      <span className={`inline-block mb-1 px-2 py-0.5 text-xs font-medium ${typeColor(ev.type)}`}>
                        {typeLabel(ev.type)}
                      </span>
                    )}
                    <p className="text-sm font-semibold leading-snug">{ev?.title || 'Unknown event'}</p>
                    {ev?.start_time && (
                      <p className="text-xs mt-0.5">
                        {fmtTime(ev.start_time)}{ev.end_time ? ` – ${fmtTime(ev.end_time)}` : ''}
                        {ev.location ? ` · ${ev.location}` : ''}
                      </p>
                    )}
                  </div>
                  <button onClick={() => removeFromPlan(ev.id)} className={BTN_GHOST}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── WearingTab ────────────────────────────────────────────────────────────────
function WearingTab({ conId }) {
  const [days, setDays] = useState([]);
  const [cosplays, setCosplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(null); // slot id whose picker is open
  const [busy, setBusy] = useState(false);
  const [newDayLabel, setNewDayLabel] = useState('');

  const [shareOpen, setShareOpen] = useState(false);
  const [shareStage, setShareStage] = useState('select'); // 'select' | 'preview'
  const [shareSel, setShareSel] = useState({});           // cosplayId -> included
  const [shareBusy, setShareBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareBlob, setShareBlob] = useState(null);

  const load = useCallback(async () => {
    try {
      const [wr, cr] = await Promise.all([
        api.get(`/cons/${conId}/wearing`),
        api.get('/kc/cosplays'),
      ]);
      setDays(wr.data?.days || []);
      setCosplays(cr.data || []);
    } catch {
      toast.error('Could not load your wearing plan');
    } finally {
      setLoading(false);
    }
  }, [conId]);

  useEffect(() => { load(); }, [load]);

  const apply = (data) => setDays(data?.days || []);

  const assign = async (slotId, cosplayId) => {
    setBusy(true);
    try {
      const res = await api.patch(`/cons/${conId}/wearing/slots/${slotId}`, { cosplay_id: cosplayId });
      apply(res.data);
      setPicker(null);
    } catch (err) {
      toast.error(errMsg(err, 'Could not assign'));
    } finally {
      setBusy(false);
    }
  };

  const addSlot = async (day) => {
    try {
      const res = await api.post(`/cons/${conId}/wearing/slots`, { date: day.date, day_label: day.label });
      apply(res.data);
    } catch (err) {
      toast.error(errMsg(err, 'Could not add an outfit'));
    }
  };

  const removeSlot = async (slotId) => {
    try {
      const res = await api.delete(`/cons/${conId}/wearing/slots/${slotId}`);
      apply(res.data);
    } catch (err) {
      toast.error(errMsg(err, 'Could not remove'));
    }
  };

  const addDay = async () => {
    const label = newDayLabel.trim();
    if (!label) return;
    try {
      const res = await api.post(`/cons/${conId}/wearing/slots`, { date: '', day_label: label });
      apply(res.data);
      setNewDayLabel('');
    } catch (err) {
      toast.error(errMsg(err, 'Could not add day'));
    }
  };

  // Distinct cosplays assigned across all slots (the share picker draws from these).
  const assignedCosplays = [];
  const seenCp = new Set();
  for (const d of days) {
    for (const s of (d.slots || [])) {
      if (s.cosplay && !seenCp.has(s.cosplay.id)) {
        seenCp.add(s.cosplay.id);
        assignedCosplays.push(s.cosplay);
      }
    }
  }

  const openShare = () => {
    const sel = {};
    assignedCosplays.forEach((c) => { sel[c.id] = true; });
    setShareSel(sel);
    setShareStage('select');
    if (shareUrl) URL.revokeObjectURL(shareUrl);
    setShareUrl(null);
    setShareBlob(null);
    setShareOpen(true);
  };

  const closeShare = () => {
    if (shareUrl) URL.revokeObjectURL(shareUrl);
    setShareUrl(null);
    setShareBlob(null);
    setShareOpen(false);
  };

  const generateShare = async () => {
    const ids = assignedCosplays.filter((c) => shareSel[c.id]).map((c) => c.id);
    if (!ids.length) { toast.error('Pick at least one cosplay'); return; }
    setShareBusy(true);
    try {
      const res = await api.post(`/cons/${conId}/cosplan-image`, { cosplay_ids: ids }, { responseType: 'blob' });
      if (shareUrl) URL.revokeObjectURL(shareUrl);
      setShareBlob(res.data);
      setShareUrl(URL.createObjectURL(res.data));
      setShareStage('preview');
    } catch (err) {
      toast.error(errMsg(err, 'Could not create the image'));
    } finally {
      setShareBusy(false);
    }
  };

  const downloadShare = () => {
    if (!shareUrl) return;
    const a = document.createElement('a');
    a.href = shareUrl;
    a.download = 'my-cosplan.png';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const nativeShare = async () => {
    if (!shareBlob) return;
    const file = new File([shareBlob], 'my-cosplan.png', { type: 'image/png' });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'My cosplan' });
      } else {
        downloadShare();
      }
    } catch {
      /* user dismissed the share sheet */
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs flex-1">
          Two outfit slots are set up for each day. Assign a cosplay to each, add more, or remove the ones you don't need.
        </p>
        <button onClick={openShare} disabled={assignedCosplays.length === 0}
          className={`${BTN_SM} shrink-0 disabled:opacity-40 disabled:cursor-not-allowed`}>
          <Share2 className="w-3.5 h-3.5" /> Share my cosplan
        </button>
      </div>

      {days.map((day) => (
        <div key={(day.date || '') + day.label} className="glass-card p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold">{day.label || 'Day'}</span>
            <button onClick={() => addSlot(day)} className={BTN_SM}>
              <Plus className="w-3.5 h-3.5" /> Add outfit
            </button>
          </div>
          <div className="space-y-2">
            {day.slots.map((slot, i) => (
              <div key={slot.id} className="win-well p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium">Outfit {i + 1}</span>
                  <button onClick={() => removeSlot(slot.id)} aria-label="Remove outfit slot"
                    className="p-1.5 -m-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {slot.cosplay ? (
                  <div className="flex items-center gap-3">
                    {slot.cosplay.cover_image ? (
                      <img src={slot.cosplay.cover_image} alt={slot.cosplay.title}
                        className="w-14 h-14 object-cover" />
                    ) : (
                      <div className="w-14 h-14 win-well flex items-center justify-center">
                        <Package className="w-6 h-6" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{slot.cosplay.title}</p>
                      {slot.cosplay.character && <p className="text-xs truncate">{slot.cosplay.character}</p>}
                    </div>
                    <button onClick={() => setPicker(picker === slot.id ? null : slot.id)} className={BTN_SM}>Change</button>
                  </div>
                ) : (
                  <button onClick={() => setPicker(picker === slot.id ? null : slot.id)}
                    className={`${BTN_SM} w-full justify-center`}>
                    <Plus className="w-3.5 h-3.5" /> Assign cosplay
                  </button>
                )}
                {picker === slot.id && (
                  <div className="mt-3 pt-3 border-t border-[var(--win-shadow)]">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs">Pick a cosplay</p>
                      {slot.cosplay && (
                        <button onClick={() => assign(slot.id, null)} className={BTN_GHOST}>Clear outfit</button>
                      )}
                    </div>
                    {cosplays.length === 0 ? (
                      <p className="text-xs">No cosplays yet. Add some in the Cosplays section first.</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto">
                        {cosplays.map((c) => (
                          <button key={c.id} disabled={busy} onClick={() => assign(slot.id, c.id)}
                            className="flex items-center gap-2 p-2 text-left disabled:opacity-50">
                            {c.cover_image ? (
                              <img src={c.cover_image} alt={c.title} className="w-10 h-10 object-cover shrink-0" />
                            ) : (
                              <div className="w-10 h-10 win-well shrink-0" />
                            )}
                            <div className="min-w-0">
                              <p className="text-xs font-semibold truncate">{c.title}</p>
                              {c.character && <p className="text-[10px] truncate">{c.character}</p>}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    <button onClick={() => setPicker(null)} className={`${BTN_GHOST} mt-2`}>Cancel</button>
                  </div>
                )}
              </div>
            ))}
            {day.slots.length === 0 && (
              <button onClick={() => addSlot(day)} className={`${BTN_SM} w-full justify-center`}>
                <Plus className="w-3.5 h-3.5" /> Add an outfit
              </button>
            )}
          </div>
        </div>
      ))}

      <div className="glass-card p-4">
        <p className="text-xs mb-2 font-medium">Add another day</p>
        <div className="flex gap-2">
          <input className={`${INPUT} flex-1`} placeholder="e.g. Thursday (pre-con night)"
            value={newDayLabel} onChange={(e) => setNewDayLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addDay(); }} />
          <button onClick={addDay} className={BTN_PRIMARY}>
            <Plus className="w-4 h-4" /> Add day
          </button>
        </div>
      </div>

      {shareOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4"
          onClick={() => !shareBusy && closeShare()}>
          <div className="glass-card p-5 w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                <Share2 className="w-5 h-5" /> Share my cosplan
              </h2>
              <button onClick={closeShare} className="win-btn p-1.5"><X className="w-5 h-5" /></button>
            </div>

            {shareStage === 'select' && (
              <>
                <p className="text-xs mb-3">Pick the cosplays to include in your shareable image.</p>
                <div className="space-y-2 mb-4">
                  {assignedCosplays.map((c) => {
                    const on = !!shareSel[c.id];
                    return (
                      <button key={c.id} onClick={() => setShareSel((s) => ({ ...s, [c.id]: !s[c.id] }))}
                        className="w-full flex items-center gap-3 p-2 text-left">
                        {c.cover_image ? (
                          <img src={c.cover_image} alt={c.title} className="w-12 h-12 object-cover shrink-0" />
                        ) : (
                          <div className="w-12 h-12 win-well flex items-center justify-center shrink-0"><Package className="w-5 h-5" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">{c.title}</p>
                          {c.character && <p className="text-xs truncate">{c.character}</p>}
                        </div>
                        <span className={`w-5 h-5 flex items-center justify-center shrink-0 win-well ${on ? 'win-selected' : ''}`}>
                          {on && <Check className="w-3 h-3" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <button onClick={generateShare} disabled={shareBusy} className={`${BTN_PRIMARY} w-full justify-center`}>
                  {shareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Create image</>}
                </button>
              </>
            )}

            {shareStage === 'preview' && (
              <>
                {shareUrl && <img src={shareUrl} alt="Cosplan preview" className="w-full win-sunken mb-4" />}
                <div className="flex gap-2">
                  <button onClick={() => setShareStage('select')} className={`${BTN_SM} shrink-0`}>← Back</button>
                  <button onClick={downloadShare} className={`${BTN_PRIMARY} flex-1 justify-center`}>
                    <Download className="w-4 h-4" /> Download
                  </button>
                  {typeof navigator !== 'undefined' && navigator.canShare && (
                    <button onClick={nativeShare} className={`${BTN_PRIMARY} flex-1 justify-center`}>
                      <Share2 className="w-4 h-4" /> Share
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── SquadTab ──────────────────────────────────────────────────────────────────
function SquadTab({ conId }) {
  const [squad, setSquad] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    try {
      const [sr, gr] = await Promise.all([
        api.get(`/cons/${conId}/squad`),
        api.get('/kc/groups'),
      ]);
      setSquad(sr.data);
      setGroups(gr.data || []);
    } catch {
      toast.error('Could not load squad');
    } finally {
      setLoading(false);
    }
  }, [conId]);

  useEffect(() => { load(); }, [load]);

  const linkGroup = async (group) => {
    setLinking(true);
    try {
      await api.put(`/cons/${conId}/squad`, { group_id: group.id, group_name: group.name });
      await load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not link group'));
    } finally {
      setLinking(false);
    }
  };

  const unlinkGroup = async () => {
    try {
      await api.delete(`/cons/${conId}/squad`);
      setSquad({ linked: false });
    } catch (err) {
      toast.error(errMsg(err, 'Could not unlink'));
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  if (squad?.linked) {
    const members = squad.members || [];
    const goingCount = members.filter((m) => m.attending).length;
    return (
      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="min-w-0">
            <p className="font-semibold truncate">{squad.group_name || 'Squad'}</p>
            <p className="text-xs mt-0.5">
              {members.length} member{members.length !== 1 ? 's' : ''}
              {goingCount > 0 && <span> · {goingCount} going</span>}
            </p>
          </div>
          <button onClick={unlinkGroup} className={BTN_GHOST} aria-label="Unlink squad"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-2">
          {members.length === 0 && <p className="text-xs">This group has no members yet.</p>}
          {members.map((m) => (
            <div key={m.user_id} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[var(--win-titlebar-start)] flex items-center justify-center text-xs font-bold text-white shrink-0">
                {(m.display_name || m.email || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm block truncate">{m.display_name || m.email}</span>
                {m.character_name && <span className="text-xs block truncate">{m.character_name}</span>}
              </div>
              {m.attending && (
                <span className="text-[10px] font-medium shrink-0">
                  ✓ Going
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {groups.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <Users className="w-8 h-8 mx-auto mb-3" />
          <p className="font-semibold">No groups yet</p>
          <p className="text-sm mt-1">Create a group in the Groups section, then link it here as your con squad.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs mb-3">Link a group to this con as your squad:</p>
          {groups.map((g) => (
            <button key={g.id} disabled={linking} onClick={() => linkGroup(g)}
              className="glass-card p-4 w-full text-left disabled:opacity-50">
              <p className="text-sm font-semibold">{g.name}</p>
              <p className="text-xs mt-0.5">{g.members?.length || 0} members</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── PackingTab ────────────────────────────────────────────────────────────────
function PackingTab({ conId }) {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState('');
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState('all');
  const [showKits, setShowKits] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ir, tr] = await Promise.all([
        api.get(`/cons/${conId}/checklist`),
        api.get('/checklist-templates'),
      ]);
      setItems(ir.data || []);
      setTemplates(tr.data || []);
    } catch {
      toast.error('Could not load your packing list');
    } finally {
      setLoading(false);
    }
  }, [conId]);

  useEffect(() => { load(); }, [load]);

  const addItem = async () => {
    if (!newItem.trim()) return;
    setAdding(true);
    try {
      const res = await api.post(`/cons/${conId}/checklist`, { text: newItem.trim() });
      setItems((i) => [...i, res.data]);
      setNewItem('');
    } catch (err) {
      toast.error(errMsg(err, 'Could not add item'));
    } finally {
      setAdding(false);
    }
  };

  const toggleItem = async (id, done) => {
    try {
      await api.patch(`/cons/${conId}/checklist/${id}`, { done: !done });
      setItems((prev) => prev.map((i) => i.id === id ? { ...i, done: !done } : i));
    } catch {
      toast.error('Could not update');
    }
  };

  const deleteItem = async (id) => {
    try {
      await api.delete(`/cons/${conId}/checklist/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch {
      toast.error('Could not delete');
    }
  };

  const applyKit = async (name) => {
    try {
      await api.post(`/cons/${conId}/checklist/apply-template`, { name });
      const res = await api.get(`/cons/${conId}/checklist`);
      setItems(res.data || []);
      setShowKits(false);
      toast.success(`Added the ${name}`);
    } catch (err) {
      toast.error(errMsg(err, 'Could not add that kit'));
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const doneCount = items.filter((i) => i.done).length;
  const matches = (i) => filter === 'all' ? true : filter === 'packed' ? i.done : !i.done;

  // Group visible items by category, preserving first-seen order.
  const groups = [];
  const byCat = {};
  for (const it of items.filter(matches)) {
    const cat = it.category || 'My Items';
    if (!byCat[cat]) { byCat[cat] = { cat, items: [] }; groups.push(byCat[cat]); }
    byCat[cat].items.push(it);
  }

  return (
    <div>
      <div className="flex gap-1.5 mb-3 overflow-x-auto scrollbar-hide">
        {[
          { key: 'all', label: `All (${items.length})` },
          { key: 'unpacked', label: `Unpacked (${items.length - doneCount})` },
          { key: 'packed', label: `Packed (${doneCount})` },
        ].map(({ key, label }) => (
          <FilterPill key={key} label={label} active={filter === key} onClick={() => setFilter(key)} />
        ))}
      </div>

      <div className="space-y-4 mb-4">
        {groups.map((g) => (
          <div key={g.cat}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-2">{g.cat}</p>
            <div className="space-y-2">
              {g.items.map((item) => (
                <div key={item.id} className="glass-card p-3 flex items-center gap-3">
                  <button onClick={() => toggleItem(item.id, item.done)}
                    className="w-5 h-5 flex items-center justify-center shrink-0">
                    {item.done && <Check className="w-3 h-3" />}
                  </button>
                  <span className={`flex-1 text-sm ${item.done ? 'line-through text-[var(--win-text-disabled)]' : ''}`}>
                    {item.text}
                  </span>
                  <button onClick={() => deleteItem(item.id)} className={BTN_GHOST}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
        {groups.length === 0 && (
          <p className="text-center text-sm py-6">
            {filter === 'packed' ? 'Nothing packed yet' : filter === 'unpacked' ? 'All packed!' : 'Your packing list is empty — add an item or a starter kit below.'}
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <input className={`${INPUT} flex-1`} placeholder="Add item…" value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }} />
        <button onClick={addItem} disabled={adding} className={BTN_PRIMARY}>
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </button>
      </div>

      {templates.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowKits((s) => !s)} className={BTN_GHOST}>
            <Plus className="w-3.5 h-3.5" /> Add a starter kit
          </button>
          {showKits && (
            <div className="flex flex-wrap gap-2 mt-2">
              {templates.map((t) => (
                <button key={t.name} onClick={() => applyKit(t.name)}
                  className="px-3 py-1.5 win-btn text-xs">
                  + {t.name} <span>({t.items.length})</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── BudgetTab ─────────────────────────────────────────────────────────────────
function BudgetTab({ conId }) {
  const [expenses, setExpenses] = useState([]);
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newExp, setNewExp] = useState({ label: '', amount: '', category: '' });
  const [adding, setAdding] = useState(false);
  const [editBudget, setEditBudget] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);

  const CATEGORIES = ['Merch', 'Food', 'Hotel', 'Travel', 'Badge', 'Costume', 'Other'];

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/cons/${conId}/budget`);
      setExpenses(res.data?.expenses || []);
      setTarget(res.data?.target ?? null);
      setEditBudget(res.data?.target != null ? String(res.data.target) : '');
    } catch {
      toast.error('Could not load your budget');
    } finally {
      setLoading(false);
    }
  }, [conId]);

  useEffect(() => { load(); }, [load]);

  const addExpense = async () => {
    if (!newExp.label.trim() || !newExp.amount) return;
    setAdding(true);
    try {
      const res = await api.post(`/cons/${conId}/budget/expenses`, {
        note: newExp.label.trim(),
        amount: parseFloat(newExp.amount),
        category: newExp.category || 'Other',
      });
      setExpenses((e) => [...e, res.data]);
      setNewExp({ label: '', amount: '', category: '' });
    } catch (err) {
      toast.error(errMsg(err, 'Could not add expense'));
    } finally {
      setAdding(false);
    }
  };

  const deleteExpense = async (id) => {
    try {
      await api.delete(`/cons/${conId}/budget/expenses/${id}`);
      setExpenses((e) => e.filter((x) => x.id !== id));
    } catch {
      toast.error('Could not delete');
    }
  };

  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      const t = parseFloat(editBudget) || 0;
      await api.put(`/cons/${conId}/budget/target`, { target: t });
      setTarget(t);
    } catch (err) {
      toast.error(errMsg(err, 'Could not save budget'));
    } finally {
      setSavingBudget(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const tgt = target || 0;
  const pct = tgt > 0 ? Math.min(100, Math.round((total / tgt) * 100)) : null;
  const over = tgt > 0 && total > tgt;

  return (
    <div className="space-y-4">
      {/* Budget target */}
      <div className="glass-card p-4">
        <p className="text-xs mb-2 font-medium">Budget target</p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm z-10">$</span>
            <input type="number" className={`${INPUT} pl-6`} placeholder="0.00"
              value={editBudget} onChange={(e) => setEditBudget(e.target.value)} />
          </div>
          <button onClick={saveBudget} disabled={savingBudget} className={BTN_PRIMARY}>
            {savingBudget ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set'}
          </button>
        </div>
        {tgt > 0 && (
          <div className="mt-3">
            <div className="flex justify-between text-xs mb-1">
              <span className={over ? 'text-[#8b0000] font-semibold' : ''}>
                ${total.toFixed(2)} spent{over ? ' — over budget' : ''}
              </span>
              <span>${tgt.toFixed(2)}</span>
            </div>
            <div className="h-2 win-sunken overflow-hidden">
              <div className={`h-full ${over ? 'bg-[#8b0000]' : 'bg-[var(--win-titlebar-start)]'}`}
                style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
        {tgt === 0 && (
          <p className="text-xs mt-2">
            Total spent: <span className="font-semibold">${total.toFixed(2)}</span>
          </p>
        )}
      </div>

      {/* Add expense */}
      <div className="glass-card p-4 space-y-2">
        <p className="text-xs font-medium mb-1">Add expense</p>
        <input className={INPUT} placeholder="What did you spend on?" value={newExp.label}
          onChange={(e) => setNewExp((n) => ({ ...n, label: e.target.value }))} />
        <div className="flex gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm z-10">$</span>
            <input type="number" className={`${INPUT} pl-6`} placeholder="0.00"
              value={newExp.amount} onChange={(e) => setNewExp((n) => ({ ...n, amount: e.target.value }))} />
          </div>
          <select className={`${INPUT} flex-1`} value={newExp.category}
            onChange={(e) => setNewExp((n) => ({ ...n, category: e.target.value }))}>
            <option value="">Category</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button onClick={addExpense} disabled={adding} className={`${BTN_PRIMARY} w-full justify-center`}>
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Add expense</>}
        </button>
      </div>

      {/* Expense list */}
      {expenses.length > 0 ? (
        <div className="space-y-2">
          {expenses.map((exp) => (
            <div key={exp.id} className="glass-card p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{exp.note}</p>
                {exp.category && <span className="text-xs">{exp.category}</span>}
              </div>
              <span className="text-sm font-semibold shrink-0">${exp.amount?.toFixed(2)}</span>
              <button onClick={() => deleteExpense(exp.id)} className={BTN_GHOST}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="glass-card p-6 text-center">
          <DollarSign className="w-7 h-7 mx-auto mb-2" />
          <p className="text-sm">No expenses yet</p>
        </div>
      )}
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────
export default function ConDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [con, setCon] = useState(null);
  const [events, setEvents] = useState([]);
  const [plan, setPlan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('browse');
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    try {
      const [cr, er, pr] = await Promise.all([
        api.get(`/cons/${id}`),
        api.get(`/cons/${id}/events`),
        api.get(`/cons/${id}/my-schedule`),
      ]);
      setCon(cr.data);
      setEvents(er.data || []);
      setPlan(pr.data || []);
    } catch {
      toast.error('Could not load convention');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const reloadPlan = useCallback(async () => {
    try {
      const res = await api.get(`/cons/${id}/my-schedule`);
      setPlan(res.data || []);
    } catch { /* keep existing plan on transient failure */ }
  }, [id]);

  const handleImported = useCallback(async () => {
    setShowImport(false);
    try {
      const res = await api.get(`/cons/${id}/events`);
      setEvents(res.data || []);
    } catch { /* silently refresh */ }
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!con) {
    return (
      <div className="glass-card p-8 text-center">
        <p>Convention not found.</p>
        <button onClick={() => navigate('/cons')} className={`${BTN_SM} mt-4 mx-auto`}>
          ← Back to Cons
        </button>
      </div>
    );
  }

  const cd = countdown(con.start_date);

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <button onClick={() => navigate('/cons')}
          className="flex items-center gap-1.5 text-sm mb-3">
          <ArrowLeft className="w-4 h-4" /> My Conventions
        </button>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-extrabold">{con.name}</h1>
            <p className="text-sm flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
              <span className="flex items-center gap-1">
                <CalendarDays className="w-3.5 h-3.5" /> {fmtRange(con.start_date, con.end_date)}
              </span>
              {(con.venue || con.city) && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> {[con.venue, con.city].filter(Boolean).join(', ')}
                </span>
              )}
            </p>
          </div>
          {cd && (
            <span className="text-xs font-medium whitespace-nowrap shrink-0">
              {cd}
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto scrollbar-hide mb-5 pb-1">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium whitespace-nowrap ${activeTab === key ? 'win-selected' : ''}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'browse' && (
        <BrowseTab conId={id} events={events} plan={plan} reloadPlan={reloadPlan}
          onImport={() => setShowImport(true)} />
      )}
      {activeTab === 'plan' && (
        <MyPlanTab conId={id} plan={plan} reloadPlan={reloadPlan} />
      )}
      {activeTab === 'wearing' && <WearingTab conId={id} />}
      {activeTab === 'squad' && <SquadTab conId={id} />}
      {activeTab === 'packing' && <PackingTab conId={id} />}
      {activeTab === 'budget' && <BudgetTab conId={id} />}

      {showImport && (
        <ImportModal conId={id} onClose={() => setShowImport(false)} onImported={handleImported} />
      )}
    </div>
  );
}
